import React, { ReactElement, useState } from 'react';
import ReactDOM from 'react-dom';
// @ts-ignore
import { Col, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TextField from '../Partials/TextField';
import { User } from '../../../structures/models/user';
import PhoneField from '../Partials/PhoneField';
import { GOOGLE_RECAPTCHA_KEY } from '../../../utils/constants';
import { BasicContactFormRequest } from '../../../structures/requests/basicContactForm';
import createBasicContactForm from '../../../utils/ajax/basicContactFormAjax';
import RadioButtonGroup from '../Partials/RadioButtonGroup';
import RecaptchaDisclaimer from '../Partials/RecaptchaDisclaimer';
import BasicContactValidation from './validation';

type BasicContactValues = {
  currentUser?: User // this is used to fill out the form with the current user's inf
};

const BasicContactForm: React.FC<BasicContactValues> = (props): ReactElement => {
  const {
    currentUser,
  } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  // we wrap executeRecaptcha because it might take a bit to initialize,
  // so we handle that possibility gracefully here
  const handleReCaptchaVerify = async () => {
    if (!executeRecaptcha) {
      return '';
    }

    return executeRecaptcha('submit');
  };

  const formProps = {
    initialValues: {
      name: currentUser ? currentUser.fullName : '',
      email: currentUser ? currentUser.email : '',
      breeder_phone: currentUser ? currentUser.phone : '',
      breeder_name: '',
      message: '',
      category: 'Questions & Comments',
    },
    validateOnChange: false,
    validateOnBlur: true,
  };
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Hide form after submission
  if (submitted) {
    return (
      <div className="alert alert-success">
        Your message has been sent. Thanks!
      </div>
    );
  }

  // Render form
  return (
    <>
      <Formik
        {...formProps}
        validationSchema={BasicContactValidation}
        onSubmit={async (values, helpers) => {
          setLoading(true);
          const newValues: BasicContactFormRequest = {
            recaptcha: await handleReCaptchaVerify(),
            ...values,
          };
          createBasicContactForm(newValues)
            .then(() => {
              setLoading(false);
              setSubmitted(true);
            }).catch((e) => {
              setLoading(false);
              if (e.response?.status === 422) {
                const { errors } = e.response.data;
                Object.keys(errors).forEach((key) => {
                  if (Object.prototype.hasOwnProperty.call(errors, key)) {
                    helpers.setFieldError(key, errors[key]);
                  }
                });
              } else {
                helpers.setFieldError('name', 'Something went wrong. Please try again.');
              }
            });
        }}
      >
        {(formikProps) => {
          const {
            handleSubmit,
            submitCount,
            errors,
          } = formikProps;
          const errorDisplay: Array<React.ReactElement> = [];
          if (submitCount) {
            Object.keys(errors).forEach((errorKey) => {
              errorDisplay.push(
                <div className="alert alert-danger w-100">
                  {errors[errorKey]}
                </div>,
              );
            });
          }
          return (
            <>
              <Row>
                <Col xs={12} md={4}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="name"
                    visibleName="Your Name"
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="email"
                    visibleName="Your Email"
                  />
                </Col>

                <Col xs={12} md={4}>
                  <PhoneField
                    showLabel={false}
                    placeholder="Breeder Phone"
                    formikProps={formikProps}
                    fieldName="breeder_phone"
                    visibleName="Breeder Phone"
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="breeder_name"
                    visibleName="Breeder Name"
                  />
                </Col>

                <Col xs={12} md={4}>
                  <RadioButtonGroup
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="category"
                    visibleName="Category"
                    possibleValues={[
                      {
                        display: 'Questions & Comments',
                        value: 'Questions & Comments',
                      },
                      {
                        display: 'Site Feedback',
                        value: 'Site Feedback',
                      },
                    ]}
                  />

                </Col>

                <Col xs={12}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="message"
                    visibleName="Message"
                    showAsTextarea={true}
                  />
                </Col>

              </Row>

              <Row>
                {errorDisplay}
              </Row>

              <Row className="pb-3">
                <Col xs={12}>
                  <span className="text-muted small">
                    By providing your phone number, you consent to receive text messages
                    from Lancaster Puppies for purposes related to our services.
                    Message frequency may vary. Message and Data Rates may apply.
                    Reply HELP for help or STOP to unsubscribe
                  </span>
                </Col>
              </Row>

              <Row className="pb-3">
                <div className="col">
                  <button
                    type="submit"
                    className="btn btn-lg btn-primary text-uppercase basic-family"
                    onClick={() => {
                      handleSubmit();
                    }}
                    disabled={loading || !executeRecaptcha}
                  >
                    Send Message
                  </button>
                </div>
              </Row>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default BasicContactForm;

const elem = document.getElementById('react-basic-contact-form');

if (elem) {
  const currentUserAttribute = elem.getAttribute('data-current-user');

  const currentUser = currentUserAttribute ? JSON.parse(currentUserAttribute) : null;

  ReactDOM.render(
    <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_KEY || ''}>
      <BasicContactForm
        currentUser={currentUser}
      />
      <RecaptchaDisclaimer />
    </GoogleReCaptchaProvider>,
    elem,
  );
}
